/**
 * SitesListPage — 고객 현장 목록 (스튜디오 홈 우측 패널 대체)
 *
 * 기존 SiteArea(우측 토글 패널)를 없애고 전용 화면으로 대체.
 * PageHeader("나의 현장 목록" + 캘린더·신호등·신규 등록) + SiteList(검색 영역 카드 + 4열 카드) + 상세/등록 모달.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Page, PageHeader } from '@indipia/ui-core';
import { SiteRegistrationPopup } from '@indipia/shared-features';
import { SignalDot } from '../todo/SignalDot';
import { SiteList } from '../../app/shell/SiteList';
import { SiteDetailPanel } from '../../app/shell/SiteDetailPanel';
import { SiteWorkspaceDrawer } from '../../app/shell/SiteWorkspaceDrawer';
import type { WorkspaceTab } from '../../app/shell/SiteWorkspaceDrawer';

export function SitesListPage() {
  const queryClient = useQueryClient();
  const [regOpen,      setRegOpen]      = useState(false);
  // 신호등 켜기 — 저장 없는 화면 로컬 상태(기획 slToggleSig 동일). 점·숫자는 목업.
  const [signalOn,     setSignalOn]     = useState(false);
  const [detailSiteId, setDetailSiteId] = useState<number | null>(null);
  // 카드 클릭(현장 선택) 또는 공사스케줄/사진첩/나의 할일 태그 클릭 → 작업물 슬라이드오버.
  // 태그 클릭이면 initialTab이 그 탭으로 지정된다. 연필(편집) 버튼은 기존 SiteDetailPanel 그대로.
  // siteStatus는 공사스케줄 탭 라벨(진행현황/완료현황) 계산용(2026-10-06 개선사항 B).
  const [workspace, setWorkspace] = useState<{
    siteId: number; siteName: string; siteStatus: string; initialTab?: WorkspaceTab;
  } | null>(null);

  return (
    <Page width="xl">
      <PageHeader
        title="나의 현장 목록"
        subtitle="등록된 현장을 확인하고 관리하세요"
        extra={
          <div className="flex items-center gap-2">
            {/* 캘린더로 보기 — 2026-10-06 개선사항 B. 전체 현장을 한눈에 보는 멀티 사이트
                캘린더는 인디플래너(스케줄러) 자체에 이미 완성돼 있어(SchedulerPage 기본 view) 새로
                만들지 않고 그 화면으로 이동만 한다. */}
            <Button type="button" variant="secondary" onClick={() => window.open('/agents/scheduler?view=calendar', '_blank', 'noopener')}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              캘린더로 보기
            </Button>
            <Button
              type="button"
              variant={signalOn ? 'primary' : 'secondary'}
              aria-pressed={signalOn}
              onClick={() => setSignalOn((v) => !v)}
            >
              <span className="inline-flex items-center gap-0.5">
                <SignalDot color="red" size={6} /><SignalDot color="yellow" size={6} />
                <SignalDot color="green" size={6} /><SignalDot color="blue" size={6} />
              </span>
              {signalOn ? '신호등 끄기' : '신호등 켜기'}
            </Button>
            <Button type="button" variant="primary" onClick={() => setRegOpen(true)}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              신규 등록
            </Button>
          </div>
        }
      />

      {/* 신호등 범례 — 켠 때만 */}
      {signalOn && (
        <div className="-mt-2 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-text-muted">
          {([['red', '조치 필요'], ['yellow', '대기'], ['green', '정상'], ['blue', '완료'], ['white', '시작 전']] as const).map(([c, t]) => (
            <span key={c} className="inline-flex items-center gap-1">
              <SignalDot color={c} size={10} />{t}
            </span>
          ))}
          <span>현장의 가장 급한 공정 색 · 나의 할일 옆 점 = 현장 신호 · 버튼 안 숫자 = 빨강 · 노랑 할 일 수</span>
        </div>
      )}

      {/* 현장 목록 */}
      <SiteList
        signalOn={signalOn}
        onSelectSite={(id, item, initialTab) => setWorkspace({ siteId: id, siteName: item.siteName, siteStatus: item.siteStatus, initialTab })}
        onEditSite={(id) => setDetailSiteId(id)}
        onAddSite={() => setRegOpen(true)}
      />

      {/* 현장 작업물 슬라이드오버 — key로 현장이 바뀌면 강제 재마운트 */}
      {workspace && (
        <SiteWorkspaceDrawer
          key={workspace.siteId}
          siteId={workspace.siteId}
          siteName={workspace.siteName}
          siteStatus={workspace.siteStatus}
          scheduleTabLabel={workspace.siteStatus === 'DONE' ? '완료현황' : '진행현황'}
          initialTab={workspace.initialTab}
          onClose={() => setWorkspace(null)}
        />
      )}

      {/* 현장 상세(편집) 팝업 */}
      {detailSiteId !== null && (
        <SiteDetailPanel
          siteId={detailSiteId}
          onClose={() => setDetailSiteId(null)}
        />
      )}

      {/* 현장 등록 팝업 */}
      {regOpen && (
        <SiteRegistrationPopup
          agentId={0}
          onClose={() => setRegOpen(false)}
          onComplete={(siteId) => {
            console.log('[SitesListPage] 현장 등록 완료 siteId:', siteId);
            queryClient.invalidateQueries({ queryKey: ['site-list'] });
            setRegOpen(false);
          }}
        />
      )}
    </Page>
  );
}
